import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { StudioCard } from '@/api/base-types';
import { useBaseMutation, useModules, useStudio, useTeamList } from '@/api/base';
import { useStudioParticipation } from '@/api/m3';
import { CHECKLIST_LEGEND, Legend, StatusDot } from '@/components/kit/Marks';
import { people, date as ruDateIso } from '@/lib/format';
import { CHECKLIST_COLUMNS } from '@/features/crm/deals/DealPage';
import { READINESS, REG_STATUS } from '@/features/crm/labels';
import { api } from '@/api/client';
import { ActionMenu, RowActions, type MenuItem } from '@/components/kit/ActionMenu';
import { Button } from '@/components/kit/Button';
import { Breadcrumbs, Kv } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { ErrorState, Note, Skeleton, TableSkeleton } from '@/components/kit/Note';
import { Overview, Panel } from '@/components/kit/Panel';
import { LinkRow, PersonHeader, Who } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { useToast } from '@/components/kit/Toast';
import { formatPhone } from '@/lib/phone';
import { titleInitials } from '@/lib/text';
import { ArchiveModal } from '../shared/ArchiveModal';
import { FeedPanel } from '../shared/FeedPanel';
import { JournalModal } from '../shared/JournalModal';
import { CONTACT_ROLE, ruDate } from '../shared/labels';
import { MergeModal } from '../shared/MergeModal';
import { useLinkActions } from '../shared/useLinkActions';
import { errorText } from '../shared/useUndoable';
import { NewTeamModal } from '../teams/TeamDialogs';
import { AddContactModal, AddTeamModal, EditStudioModal } from './StudioDialogs';
import { NickLink } from '../shared/NickLink';

type Dialog = 'edit' | 'contact' | 'team' | 'newTeam' | 'merge' | 'journal' | 'archive';

/**
 * Карточка студии (БЛ 11.4, viewStudio в макете). «Финансы», «История
 * участия», цифры «Участвуют сейчас», «Оплачено всего», «Долг» и участие
 * в таблице команд — со своими модулями (ТЗ M2 7.3).
 */
export function StudioPage() {
  const { studioId } = useParams();
  const navigate = useNavigate();
  const studio = useStudio(studioId);
  const modules = useModules();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);
  const crumbs = (label: string) => (
    <Breadcrumbs items={[{ label: 'Студии', onClick: () => navigate('/studios') }, { label }]} />
  );

  if (studio.isError) {
    return (
      <div className="main">
        {crumbs('Студия')}
        <ErrorState message={studio.error.message} onRetry={() => void studio.refetch()} />
      </div>
    );
  }
  if (studio.isLoading || !studio.data) {
    return (
      <div className="main">
        {crumbs('…')}
        <Skeleton width={320} height={28} />
      </div>
    );
  }

  const s = studio.data;
  return (
    <div className="main has-abar">
      {crumbs(s.name)}
      <StudioHeader studio={s} onDialog={setDialog} primaryEdit={!modules.data?.tasks} />

      <MetricRow
        variant="sum"
        items={[
          { label: 'Команд', value: s.teamsCount, dim: !s.teamsCount },
          { label: 'Участников', value: s.participantsCount, dim: !s.participantsCount },
        ]}
      />

      <div className="stack">
        <StudioTeams studio={s} onAdd={() => setDialog('team')} withRegs={Boolean(modules.data?.registrations)} />
        <Overview
          left={
            <>
              <Panel title="Данные">
                <Kv k="Название">{s.name}</Kv>
                <Kv k="Город">{s.city.name}</Kv>
                <Kv k="Instagram">
                  <NickLink nick={s.instagram} network="instagram" />
                </Kv>
                <Kv k="Год основания">{s.foundedYear ?? <span className="dim">не указан</span>}</Kv>
                <Kv k="В базе с">
                  <span className="num">{ruDate(s.createdAt)}</span>
                </Kv>
              </Panel>
              <ContactsPanel studio={s} onAdd={() => setDialog('contact')} />
            </>
          }
          right={<FeedPanel entity="studio" id={s.id} placeholder="Что важно знать о студии" />}
        />
      </div>

      {dialog === 'edit' ? <EditStudioModal studio={s} onClose={close} /> : null}
      {dialog === 'contact' ? <AddContactModal studio={s} onClose={close} /> : null}
      {dialog === 'team' ? <AddTeamModal studio={s} onClose={close} onNew={() => setDialog('newTeam')} /> : null}
      {dialog === 'newTeam' ? (
        <NewTeamModal
          studio={{ id: s.id, name: s.name }}
          city={{ id: s.city.id, name: s.city.name }}
          onClose={close}
          onCreated={(id) => navigate(`/teams/${id}`)}
        />
      ) : null}
      {dialog === 'journal' ? <JournalModal entity="studio" id={s.id} onClose={close} /> : null}
      {dialog === 'archive' ? <ArchiveModal entity="studio" id={s.id} name={s.name} onClose={close} /> : null}
      {dialog === 'merge' ? <MergeModal entity="studio" id={s.id} onClose={close} /> : null}
    </div>
  );
}

function StudioHeader({
  studio: s,
  onDialog,
  primaryEdit,
}: {
  studio: StudioCard;
  onDialog: (d: Dialog) => void;
  primaryEdit: boolean;
}) {
  const toast = useToast();
  const unarchive = useBaseMutation(() => api.post(`/studios/${s.id}/unarchive`));
  const items: MenuItem[] = [
    { label: 'Слить с дублем…', onClick: () => onDialog('merge') },
    { label: 'Журнал изменений', onClick: () => onDialog('journal') },
    s.archivedAt
      ? {
          label: 'Вернуть из архива',
          onClick: () =>
            unarchive.mutate(undefined, {
              onSuccess: () => toast.show('Студия снова в работе'),
              onError: (e) => toast.showError(errorText(e)),
            }),
        }
      : { label: 'В архив…', danger: true, onClick: () => onDialog('archive') },
  ];
  return (
    <PersonHeader
      initials={s.initials}
      title={s.name}
      pills={s.archivedAt ? <Pill tone="mut">В архиве</Pill> : null}
      meta={
        <>
          <span>{s.city.name}</span>
          {s.foundedYear ? <span>основана в {s.foundedYear}</span> : null}
          <span>в базе с {ruDate(s.createdAt)}</span>
        </>
      }
      actions={
        <>
          {primaryEdit ? (
            <Button tone="primary" onClick={() => onDialog('edit')}>
              Изменить
            </Button>
          ) : null}
          <ActionMenu items={items} />
        </>
      }
    />
  );
}

/**
 * Команды студии. С модулем «Регистрации» — текущее участие каждой команды
 * с точками чек-листа (ТЗ M3 7.2, tRow в макете M2); без него — строка
 * списка команд.
 */
function StudioTeams({ studio, onAdd, withRegs }: { studio: StudioCard; onAdd: () => void; withRegs: boolean }) {
  const navigate = useNavigate();
  const teams = useTeamList({ studioId: studio.id, limit: 50 });
  const regs = useStudioParticipation(studio.id, withRegs);
  const items = teams.data?.items ?? [];
  const anyReg = Object.values(regs.data ?? {}).some(Boolean);
  return (
    <Panel
      flush
      title="Команды"
      actions={
        <Button small onClick={onAdd}>
          Добавить команду
        </Button>
      }
    >
      {teams.isError ? (
        <ErrorState message={teams.error.message} onRetry={() => void teams.refetch()} />
      ) : teams.isLoading ? (
        <TableSkeleton rows={3} cols={4} />
      ) : !items.length ? (
        <div className="empty">У студии пока нет команд.</div>
      ) : withRegs ? (
        <>
          <table className="tbl stt">
            <thead>
              <tr>
                <th className="c-sten">Команда</th>
                <th className="c-scur">Текущее участие</th>
                {CHECKLIST_COLUMNS.map((c) => (
                  <th key={c.key} className="rc">
                    {c.label}
                  </th>
                ))}
                <th className="c-sreg">Регистрация</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const r = regs.data?.[t.id] ?? null;
                const st = r ? (r.status === 'active' ? READINESS[r.readiness] : REG_STATUS[r.status]) : null;
                return (
                  <tr key={t.id} className="clk" onClick={() => navigate(`/teams/${t.id}`)}>
                    <td data-col="primary">
                      <Who initials={t.initials} name={t.name} sub={<div className="sub">{people(t.membersCount)}</div>} />
                    </td>
                    <td data-col="hide">
                      {r ? (
                        <>
                          <div className="nw">
                            {r.event.city} · {ruDateIso(r.event.dateFrom).slice(0, 5)}
                          </div>
                          <div className="sub nw">{r.nomination.name}</div>
                        </>
                      ) : (
                        <span className="dim">не участвует</span>
                      )}
                    </td>
                    {CHECKLIST_COLUMNS.map((c) => (
                      <td key={c.key} className="rc" data-col="hide">
                        {r ? <StatusDot state={r.checklist[c.key]} /> : null}
                      </td>
                    ))}
                    <td data-col="secondary">{st ? <Pill tone={st.tone}>{st.label}</Pill> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {anyReg ? <Legend items={CHECKLIST_LEGEND} /> : null}
        </>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th className="c-tname srt">
                Команда <span className="ar">▲</span>
              </th>
              <th className="c-tcity">Город</th>
              <th className="c-thead">Руководитель</th>
              <th className="r c-tnum">Участников</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="clk" onClick={() => navigate(`/teams/${t.id}`)}>
                <td data-col="primary">
                  <Who
                    initials={t.initials}
                    name={t.name}
                    sub={t.signal === 'awaiting_head' ? <div className="sub wtx">данные ждут руководителя</div> : null}
                  />
                </td>
                <td data-col="hide">{t.city.name}</td>
                <td data-col="hide">
                  {t.head.shortName}
                  <div className="sub num">{formatPhone(t.head.phone)}</div>
                </td>
                <td className="r num" data-col="secondary">
                  {t.membersCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function ContactsPanel({ studio: s, onAdd }: { studio: StudioCard; onAdd: () => void }) {
  const navigate = useNavigate();
  const links = useLinkActions();
  return (
    <Panel
      title="Контакты"
      count={s.contacts.length}
      actions={
        <Button small onClick={onAdd}>
          Добавить контакт
        </Button>
      }
    >
      {s.contacts.length ? (
        s.contacts.map((c) => (
          <LinkRow
            key={c.id}
            initials={titleInitials(c.client.fullName)}
            name={c.client.shortName}
            sub={[CONTACT_ROLE[c.role], c.isMain ? 'основной контакт' : '', formatPhone(c.client.phone)]
              .filter(Boolean)
              .join(' · ')}
            value={<RowActions items={links.contact(c)} />}
            onClick={() => navigate(`/clients/${c.client.id}`)}
          />
        ))
      ) : (
        <Note tone="warn">
          <b>Контактов нет.</b> Студии некому писать — добавьте руководителя или администратора.
        </Note>
      )}
    </Panel>
  );
}
